import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { IconCheck, IconChevronDown } from '../Icons';

export default function ClientAccountPicker({
  options = [],
  value = [],
  onChange,
  placeholder = 'Select account',
  className = '',
  /** Shown when value id is not in options (e.g. admin viewing another user's account). */
  selectedOption = null,
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const listId = useId();

  const selectedId = value[0] || null;

  const triggerLabel = useMemo(() => {
    if (!options.length && !selectedOption) return placeholder;
    if (!selectedId) return placeholder;
    const fromOptions = options.find((option) => option.id === selectedId);
    if (fromOptions?.name) return fromOptions.name;
    if (selectedOption?.id === selectedId && selectedOption?.name) return selectedOption.name;
    return placeholder;
  }, [options, placeholder, selectedId, selectedOption]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    }

    function handleEscape(event) {
      if (event.key === 'Escape') setOpen(false);
    }

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  function selectOne(id) {
    onChange([id]);
    setOpen(false);
  }

  return (
    <div className={`vd-select vd-client-select-full ${className}${open ? ' open' : ''}`} ref={rootRef}>
      <button
        type="button"
        className="vd-select-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        disabled={options.length === 0 && !selectedId}
        onClick={() => setOpen((prev) => !prev)}
      >
        <span className={selectedId ? 'vd-select-value' : 'vd-select-placeholder'}>{triggerLabel}</span>
        <IconChevronDown size={16} className="vd-select-chevron" />
      </button>

      {open && options.length > 0 && (
        <div id={listId} className="vd-select-menu vd-account-picker-menu" role="listbox">
          {options.map((option) => {
            const checked = option.id === selectedId;
            return (
              <button
                key={option.id}
                type="button"
                role="option"
                aria-selected={checked}
                className={`vd-account-picker-row${checked ? ' active' : ''}`}
                onClick={() => selectOne(option.id)}
              >
                <span>{option.name}</span>
                {checked && <IconCheck size={14} className="vd-select-option-check" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
